import React, { useState, useEffect, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { supabase } from './supabaseClient';
import { inject } from '@vercel/analytics';
import { DataProvider } from './DataContext';
import Auth from './components/Auth';
import Welcome from './components/Welcome';
import ProfileSetup from './components/ProfileSetup';
import Discover from './components/Discover';
import Pokes from './components/Pokes';
import HamburgerMenu from './components/HamburgerMenu';
import KofiLink from './components/KofiLink';
import Banner from './components/Banner';
import './App.css';
import { Buffer } from 'buffer';
window.Buffer = Buffer;

inject();

function AppContent() {
  const navigate = useNavigate();
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [profileComplete, setProfileComplete] = useState(false);
  const [unreadPokes, setUnreadPokes] = useState(0);
  const initialCheckDone = useRef(false);

  useEffect(() => {
  let mounted = true;
  
  const checkProfileComplete = async (email) => {
    if (!mounted) return;
    
    try {
      // Single query with both checks
      const [{ data: userData }, { count: tagCount }] = await Promise.all([
        supabase
          .from('users')
          .select('username, banned')
          .eq('email', email)
          .single(),
        supabase
          .from('user_tags')
          .select('*', { count: 'exact', head: true })
          .eq('user_email', email)
      ]);

      // Redirect banned users
      if (userData?.banned) {
        await supabase.auth.signOut();
        alert('Your account has been banned.');
        return;
      }

      const isComplete = userData?.username && tagCount > 0;
      setProfileComplete(isComplete);
      
      if (!initialCheckDone.current) {
        navigate(isComplete ? '/discover' : '/welcome');
        initialCheckDone.current = true;
      }
    } catch (error) {
      console.error('Error checking profile:', error);
    } finally {
      if (mounted) setLoading(false);
    }
  };

  const loadUnreadPokes = async (email) => {
    if (!mounted) return;
    
    try {
      const { count } = await supabase
        .from('pokes')
        .select('*', { count: 'exact', head: true })
        .eq('to_user', email)
        .eq('seen', false);
      
      if (mounted) setUnreadPokes(count || 0);
    } catch (error) {
      console.error('Error loading pokes:', error);
    }
  };

  supabase.auth.getSession().then(({ data: { session } }) => {
    if (!mounted) return;
    setSession(session);
    if (session) {
      checkProfileComplete(session.user.email);
      loadUnreadPokes(session.user.email);
    } else {
      setLoading(false);
    }
  });

  const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
    if (!mounted) return;
    setSession(session);
    if (session && !initialCheckDone) {
      checkProfileComplete(session.user.email);
      loadUnreadPokes(session.user.email);
    }
  });

  return () => {
    mounted = false;
    subscription.unsubscribe();
  };
}, [navigate]);

  // Poll for new pokes every 30 seconds
  useEffect(() => {
    if (!session) return;

    const pollPokes = async () => {
      try {
        const { data, error } = await supabase
          .from('pokes')
          .select('from_user', { count: 'exact' })
          .eq('to_user', session.user.email)
          .eq('seen', false);

        if (!error && data) {
          setUnreadPokes(data.length);
        }
      } catch (error) {
        console.error('Error polling pokes:', error);
      }
    };

    const interval = setInterval(pollPokes, 30000);

    return () => clearInterval(interval);
  }, [session]);

  const handleWelcomeContinue = () => {
    navigate('/setup');
  };

  const handleProfileComplete = () => {
    setProfileComplete(true);
    navigate('/discover');
  };

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    setProfileComplete(false);
    navigate('/');
    setUnreadPokes(0);
  };

  const handleViewPokes = () => {
    navigate('/pokes');
    // Reload unread count when viewing pokes
    if (session) {
      setTimeout(async () => {
        const { data, error } = await supabase
          .from('pokes')
          .select('from_user', { count: 'exact' })
          .eq('to_user', session.user.email)
          .eq('seen', false);

        if (!error && data) {
          setUnreadPokes(data.length);
        }
      }, 1000);
    }
  };

  if (loading) {
    return (
      <div className="loading">
        <p>loading...</p>
      </div>
    );
  }

  if (!session) {
    return <Auth />;
  }

  return (
    <div className="app">
      {session && <Banner onHomeClick={() => navigate('/discover')} />}
      
      <HamburgerMenu
        onEditProfile={() => navigate('/profile')}
        onViewPokes={handleViewPokes}
        onSettings={() => navigate('/settings')}
        onSignOut={handleSignOut}
        unreadPokes={unreadPokes}
      />

      <Routes>
        <Route path="/welcome" element={<Welcome onContinue={handleWelcomeContinue} />} />
        <Route path="/profile" element={<ProfileSetup session={session} onComplete={handleProfileComplete} onBack={() => navigate('/discover')} isFirstTime={false} />} />
        <Route path="/setup" element={<ProfileSetup session={session} onComplete={handleProfileComplete} onBack={null} isFirstTime={true} />} />
        <Route path="/discover" element={profileComplete ? <Discover session={session} /> : <Navigate to="/welcome" />} />
        <Route path="/pokes" element={<Pokes session={session} />} />
        <Route path="/settings" element={<div className="settings-placeholder"><div style={{ padding: '100px 20px', textAlign: 'center', color: '#666' }}><p>settings coming soon</p></div></div>} />
        <Route path="/" element={<Navigate to="/discover" />} />
      </Routes>

      <KofiLink />
    </div>
  );
}

function App() {
  return (
    <Router>
      <DataProvider>
        <AppContent />
      </DataProvider>
    </Router>
  );
}

export default App;